import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppAuthService, LowScoreFallbackRequiredError } from './app-auth.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { GoogleSigninButtonComponent } from './google-signin-button.component';
import { GoogleIdTokenService } from './google-id-token.service';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { NotificationService } from '../service/notification.service';
import { FormActionsComponent } from '../form-actions.component';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationService } from '../service/translation-service.service';
import { RecaptchaV2FallbackComponent } from './recaptcha-v2-fallback.component';

function isAccountDisabledError(err: unknown): boolean {
  const e = err as { status?: number; error?: { error_type?: string } };
  return e?.status === 403 && e?.error?.error_type === 'account_disabled';
}

@Component({
  selector: 'app-request-register',
  templateUrl: 'register.component.html',
  styleUrls: ['login-reset-register.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    GoogleSigninButtonComponent,
    RouterModule,
    FormActionsComponent,
    TranslateModule,
    RecaptchaV2FallbackComponent,
  ],
})
export class RegisterComponent implements OnInit {
  private readonly authService = inject(AppAuthService);
  private readonly toastr = inject(NotificationService);
  private readonly googleSignIn = inject(GoogleIdTokenService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslationService);
  private readonly router = inject(Router);
  route = inject(ActivatedRoute);

  get backToLoginQueryParams() {
    const returnUrl = this.route.snapshot?.queryParamMap?.get?.('returnUrl') ?? null;
    return returnUrl ? { returnUrl } : {};
  }
  form = new FormBuilder().nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    name: ['', Validators.required],
  });
  googleToken?: string;
  showV2Fallback = false;
  recaptchaNeedsAttention = false;
  private pendingEmail: string | null = null;
  private pendingName: string | null = null;
  private pendingGoogleToken: string | undefined = undefined;

  async register() {
    const form = this.form.value;
    this.form.disable();
    try {
      if (this.googleToken) {
        await this.authService.registerRequest(undefined, undefined, this.googleToken);
      } else {
        await this.authService.registerRequest(form.email!, form.name!, undefined);
      }
      this.toastr.success('Please_check_your_emails_tr');
      this.form.markAsPristine();
    } catch (_error) {
      if (!this.googleToken && _error instanceof LowScoreFallbackRequiredError) {
        this.pendingEmail = form.email!;
        this.pendingName = form.name!;
        this.pendingGoogleToken = undefined;
        this.showV2Fallback = true;
        this.recaptchaNeedsAttention = true;
        this.form.enable();
        return;
      }
      if (isAccountDisabledError(_error)) {
        this.form.enable();
        await this.router.navigate(['/waiting']);
        return;
      }
      console.log(_error);
      this.form.enable();
    }
  }

  async onV2Solved(token: string) {
    try {
      await this.authService.registerRequest(
        this.pendingEmail!,
        this.pendingName!,
        this.pendingGoogleToken,
        token
      );
      this.showV2Fallback = false;
      this.recaptchaNeedsAttention = false;
      this.pendingEmail = null;
      this.pendingName = null;
      this.pendingGoogleToken = undefined;
      this.toastr.success('Please_check_your_emails_tr');
      this.form.markAsPristine();
    } catch (_error) {
      if (_error instanceof LowScoreFallbackRequiredError) {
        // server rejected v2 token too — leave widget visible so user can retry
        return;
      }
      this.showV2Fallback = false;
      this.recaptchaNeedsAttention = false;
      this.form.enable();
      if (isAccountDisabledError(_error)) {
        await this.router.navigate(['/waiting']);
        return;
      }
      console.log(_error);
    }
  }

  onV2Failed() {
    this.recaptchaNeedsAttention = true;
  }

  ngOnInit(): void {
    // Popup result or relayed token alike (app#397): the token goes out as `googleToken`, nothing else.
    this.googleSignIn
      .signIns()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (event) => {
      if ('error' in event) {
        this.googleToken = undefined;
        await this.toastr.error('login_error_occurred_tr');
        return;
      }
      this.googleToken = event.idToken;
      await this.register();
    });
  }
}
